import { isNativeApp } from '../utils/platform';
import { selectMessage } from '../config/notificationMessages';

/**
 * Local push notifications service (Capacitor).
 * All functions no-op on web. Follows the same lazy-import pattern as revenuecat.ts.
 */

// ---------------------------------------------------------------------------
// Lazy import — resolved only on native
// ---------------------------------------------------------------------------

let _LocalNotifications: typeof import('@capacitor/local-notifications').LocalNotifications | null = null;

async function loadLocalNotifications(): Promise<void> {
  if (!_LocalNotifications) {
    const mod = await import('@capacitor/local-notifications');
    _LocalNotifications = mod.LocalNotifications;
  }
}

// ---------------------------------------------------------------------------
// localStorage preferences (matches wsle: prefix convention from sound.ts)
// ---------------------------------------------------------------------------

const ENABLED_KEY = 'wsle:notificationsEnabled';
const TIME_KEY = 'wsle:notificationTime';
const DEFAULT_TIME = '09:00';
const NOTIFICATION_ID = 42001;

export function getNotificationsEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === 'true';
  } catch {
    return false;
  }
}

function setNotificationsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, String(enabled));
  } catch { /* ignore */ }
}

export function getNotificationTime(): string {
  try {
    return localStorage.getItem(TIME_KEY) || DEFAULT_TIME;
  } catch {
    return DEFAULT_TIME;
  }
}

function setNotificationTime(time: string): void {
  try {
    localStorage.setItem(TIME_KEY, time);
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function checkPermissions(): Promise<'granted' | 'denied' | 'prompt'> {
  if (!isNativeApp()) return 'denied';

  try {
    await loadLocalNotifications();
    const result = await _LocalNotifications!.checkPermissions();
    return result.display as 'granted' | 'denied' | 'prompt';
  } catch {
    return 'denied';
  }
}

export async function requestPermissions(): Promise<boolean> {
  if (!isNativeApp()) return false;

  try {
    await loadLocalNotifications();
    const result = await _LocalNotifications!.requestPermissions();
    return result.display === 'granted';
  } catch {
    return false;
  }
}

/**
 * Schedule a repeating daily notification at the user's preferred time.
 * Cancels any existing reminder first.
 */
export async function scheduleDailyReminder(streakCount: number = 0): Promise<void> {
  if (!isNativeApp()) return;

  try {
    await loadLocalNotifications();
    await cancelDailyReminder();

    const time = getNotificationTime();
    const [hourStr, minuteStr] = time.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    const body = selectMessage(streakCount);

    await _LocalNotifications!.schedule({
      notifications: [
        {
          id: NOTIFICATION_ID,
          title: 'Wallstreetle',
          body,
          schedule: {
            on: { hour, minute },
            repeats: true,
            allowWhileIdle: true,
          },
          sound: 'default',
          smallIcon: 'ic_stat_icon',
          iconColor: '#16a34a',
        },
      ],
    });
  } catch (error) {
    console.error('[Notifications] Failed to schedule daily reminder:', error);
  }
}

export async function cancelDailyReminder(): Promise<void> {
  if (!isNativeApp()) return;

  try {
    await loadLocalNotifications();
    await _LocalNotifications!.cancel({ notifications: [{ id: NOTIFICATION_ID }] });
  } catch {
    // ignore — may not exist yet
  }
}

/**
 * Called after a game completes to update tomorrow's message with current streak.
 */
export async function rescheduleAfterPlay(streakCount: number): Promise<void> {
  if (!isNativeApp() || !getNotificationsEnabled()) return;
  await scheduleDailyReminder(streakCount);
}

/**
 * Register listener for notification taps (brings app to foreground).
 */
export async function registerNotificationListeners(): Promise<void> {
  if (!isNativeApp()) return;

  try {
    await loadLocalNotifications();
    _LocalNotifications!.addListener('localNotificationActionPerformed', () => {
      // App will be brought to foreground automatically by iOS.
      // No additional navigation needed — user lands on last-viewed screen.
    });
  } catch {
    // ignore
  }
}

/**
 * Boot-time initialization: if notifications are enabled, schedule + register listeners.
 */
export async function initializeNotifications(): Promise<void> {
  if (!isNativeApp()) return;

  if (getNotificationsEnabled()) {
    const status = await checkPermissions();
    if (status === 'granted') {
      await scheduleDailyReminder();
      await registerNotificationListeners();
    } else {
      // Permission was revoked in iOS Settings — disable locally
      setNotificationsEnabled(false);
    }
  }
}

/**
 * Enable notifications: request permission, save preference, schedule.
 * Returns true if permission was granted.
 */
export async function enableNotifications(streakCount: number = 0): Promise<boolean> {
  if (!isNativeApp()) return false;

  const granted = await requestPermissions();
  if (!granted) return false;

  setNotificationsEnabled(true);
  await scheduleDailyReminder(streakCount);
  await registerNotificationListeners();
  return true;
}

/**
 * Disable notifications: cancel scheduled, save preference.
 */
export async function disableNotifications(): Promise<void> {
  setNotificationsEnabled(false);
  await cancelDailyReminder();
}

/**
 * Update the reminder time. Reschedules if notifications are enabled.
 */
export async function updateReminderTime(time: string, streakCount: number = 0): Promise<void> {
  setNotificationTime(time);
  if (getNotificationsEnabled()) {
    await scheduleDailyReminder(streakCount);
  }
}
